import React from 'react';

import { makeUclComponent } from '@rbilabs/universal-components';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Currency from 'components/currency';
import Dialog from 'components/dialog';
import Link from 'components/link';
import { routes } from 'navigation/routing';
import { t, tRich } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';
import { platform } from 'utils/environment';

import { useActiveTieredDeliveryFees } from '../hooks/use-active-tiered-delivery-fees';

import { BodyContainer, ItemContainer, SubtitleContainer } from './styled';
import { IDeliveryFeesModalProps } from './types';

const TermsLink = makeUclComponent(Link).withConfig({
  color: primitive.$fieryRed,
});

const DeliveryFeesModal: React.FC<React.PropsWithChildren<IDeliveryFeesModalProps>> = ({
  open,
  onClose,
  subTotalCents,
  deliveryFeeCents,
  smallCartFeeCents,
  serviceFeeCents,
  geographicalFeeCents,
  isLoading,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { smallCartFeeThreshold, tier } = useActiveTieredDeliveryFees({
    subTotalCents,
    platform: platform(),
  });

  if (!open) {
    return null;
  }
  return (
    <Dialog
      showDialog={open}
      testID="delivery-fees-modal"
      heading={t('deliveryFees')}
      bodyComponent={
        <BodyContainer>
          <ItemContainer testID="delivery-fee-item">
            <SubtitleContainer>
              {t('deliveryFee')}
              {': '}
              <Currency amount={deliveryFeeCents} isLoading={isLoading} />
            </SubtitleContainer>
            {tRich('deliveryFeeDescription', {
              link: <TermsLink to={routes.deliveryTerms}>{t('here')}</TermsLink>,
            })}
          </ItemContainer>
          {geographicalFeeCents > 0 && (
            <ItemContainer testID="geographical-fee-item">
              <SubtitleContainer>
                {t('geographicalFee')}
                {': '}
                <Currency amount={geographicalFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {t('geographicalFeeDescription', {
                geographicalFeeAmount: centsToDollarString(geographicalFeeCents),
              })}
            </ItemContainer>
          )}
          {serviceFeeCents > 0 && (
            <ItemContainer testID="service-fee-item">
              <SubtitleContainer>
                {t('serviceFee')}
                {': '}
                <Currency amount={serviceFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {t('serviceFeeDescription', {
                percent: tier.serviceFeePercent,
              })}
            </ItemContainer>
          )}
          {smallCartFeeCents > 0 && (
            <ItemContainer testID="small-cart-fee-item">
              <SubtitleContainer>
                {t('smallCartFee')}
                {': '}
                <Currency amount={smallCartFeeCents} isLoading={isLoading} />
              </SubtitleContainer>
              {smallCartFeeThreshold > 0 &&
                t('smallCartFeeDescription', {
                  amount: formatCurrencyForLocale(smallCartFeeThreshold),
                })}
            </ItemContainer>
          )}
        </BodyContainer>
      }
      actions={
        <ActionButton
          onPress={onClose}
          testID="delivery-fees-modal-ok-btn"
          variant={ActionButtonVariants.TEXT_ONLY}
        >
          {t('okayButtonText')}
        </ActionButton>
      }
      modalAppearanceEventMessage="Delivery Fees Modal"
      onDismiss={onClose}
    />
  );
};

export default DeliveryFeesModal;
